import React, { useRef, forwardRef, useState, ChangeEvent } from 'react';
import './ScheduleVirtualMeetingModal.scss';
import Rodal from '../../../../components/Rodal/Rodal';
import {
    Button,
    Col,
    FormGroup,
    InputGroup,
    InputGroupAddon,
    ModalBody,
    ModalHeader,
    Row,
} from 'reactstrap';
import { Form, Input } from '@availity/form';
import * as yup from 'yup';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faTrash,
    faDownload,
    faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useStateSelector } from '../../../../store/selectors';
import { useDispatch } from 'react-redux';
import { TimeZoneHelper, TimeZone } from '../../../../utils/timeZoneHelper';
import {
    MeetingParticipantsType,
    getMeetingParticipantsTypesOptionsForUser,
} from '../../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { MeetingFormat } from '../../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { MeetingScheduleType } from '../../../../slices/marketplace/meetings/models/enums/meeting-schedule-type.enum';
import createNotification from '../../../../utils/createNotification';
import { DateTimeHelper } from '../../../../utils/dateTimeHelper';
import { AddEngagementConference } from '../../../../slices/marketplace/meetings/thunks/add-engagement-conferences.thunk';
import { FetchEngagementConferences } from '../../../../slices/marketplace/meetings/thunks/fetch-engagement-conferences.thunk';
import { UpdateSupportMeeting } from '../../../../slices/marketplace/meetings/thunks/update-support-meeting.thunk';
import { PostEngagementConferenceContract } from '../../../../slices/marketplace/meetings/models/post-engagement-conference-contract';
import { UpdateSupportMeetingContract } from '../../../../slices/marketplace/meetings/models/update-support-meeting-contract';
import { EngagementMeeting } from '../../../../slices/marketplace/meetings/models/get-engagement-conference';
import { EngagementMeetingStatus } from '../../../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import {
    MeetingRecurrenceFrequency,
    MEETING_RECURRENCE_FREQUENCY_LABELS,
} from '../../../../slices/marketplace/meetings/models/enums/meeting-recurrence-frequency.enum';
import { MeetingRecurrenceContract } from '../../../../slices/marketplace/meetings/models/meeting-recurrence-contract';
import { MeetingSeriesScopeSelector } from '../MeetingSeriesScopeSelector/MeetingSeriesScopeSelector';
import moment from 'moment';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import PortalTooltip from '../../../../components/PortalTooltip/PortalTooltip';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';

interface ScheduleVirtualMeetingModalProps {
    engagementId: number;
    meetingScheduleType: MeetingScheduleType;
    isAdmin: boolean;
    isBroker: boolean;
    isBrokerExist: boolean;
    availableBuyers?: { id: number; name: string; isDataNdaSigned?: boolean }[];
    isMarketplaceBuyerContext?: boolean;
    marketplaceBuyerId?: number;
    meetingToEdit?: EngagementMeeting;
    allowedParticipantTypes?: MeetingParticipantsType[];
    onMeetingUpdated?(): void;
    onClose(): void;
}

const BUYER_REQUIRED_PARTICIPANT_TYPES: (MeetingParticipantsType | '')[] = [
    MeetingParticipantsType.BuyerAdminSupport,
    MeetingParticipantsType.BuyerAdvisor,
    MeetingParticipantsType.BuyerSellerAdvisor,
];

enum RecurrenceEndType {
    OnDate = 'date',
    AfterCount = 'count',
}

const MINIMUM_OCCURRENCE_COUNT = 2;
const MAXIMUM_OCCURRENCE_COUNT: Record<MeetingRecurrenceFrequency, number> = {
    [MeetingRecurrenceFrequency.Weekly]: 52,
    [MeetingRecurrenceFrequency.Monthly]: 12,
};
const RECURRENCE_END_DATE_HOUR = 12;
const RECURRENCE_END_TOOLTIP_ID = 'recurrence-end-tooltip';
const RECURRENCE_END_TOOLTIP_DELAY_MS = 200;
const RECURRENCE_DATE_FORMAT = 'MM/D/YYYY';
const ONE_HOUR_MS = 60 * 60 * 1000;

const DateControlInput = forwardRef(
    ({ value, placeholder, onClick }: any, ref: any) => (
        <div ref={ref} className={'btn'} onClick={onClick}>
            {value || <span className="date-placeholder">{placeholder}</span>}
        </div>
    )
);

const ScheduleVirtualMeetingModal = (
    props: ScheduleVirtualMeetingModalProps
) => {
    const dispatch = useDispatch() as any;
    const currentUser = useStateSelector((state) => state.currentUser.user);
    const axios = useStateSelector((state) => state.core.axios);
    const modalForm = useRef(null);
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);
    const recurrenceEndDateRef = useRef(null);

    const isEditMode = !!props.meetingToEdit;
    const isStartLocked =
        isEditMode &&
        props.meetingToEdit.status === EngagementMeetingStatus.InProgress;

    const [meetingTitle, setMeetingTitle] = useState(
        props.meetingToEdit?.name ?? ''
    );
    const [message, setMessage] = useState(props.meetingToEdit?.message ?? '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDiscardConfirmationVisible, setIsDiscardConfirmationVisible] =
        useState(false);

    const getParticipantsTypeOptions = () =>
        getMeetingParticipantsTypesOptionsForUser(
            props.isAdmin,
            props.isBroker,
            props.isBrokerExist,
            props.allowedParticipantTypes
        );

    const getSelectableBuyers = () =>
        (props.availableBuyers ?? []).filter(
            (buyer) =>
                participantsType !== MeetingParticipantsType.BuyerSellerAdvisor ||
                buyer.isDataNdaSigned
        );

    const getInitialParticipantsType = (): MeetingParticipantsType | '' => {
        if (isEditMode) {
            return '';
        }
        const options = getParticipantsTypeOptions();
        const optionKeys = Object.keys(options).map(Number);
        if (optionKeys.length === 1) {
            return optionKeys[0] as MeetingParticipantsType;
        }
        return '';
    };

    const [participantsType, setParticipantsType] = useState<
        MeetingParticipantsType | ''
    >(() => getInitialParticipantsType());
    const [selectedBuyerId, setSelectedBuyerId] = useState<number | ''>('');
    const [repeatFrequency, setRepeatFrequency] = useState<
        MeetingRecurrenceFrequency | ''
    >('');
    const [recurrenceEndType, setRecurrenceEndType] =
        useState<RecurrenceEndType>(RecurrenceEndType.OnDate);
    const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | null>(
        null
    );
    const [occurrenceCount, setOccurrenceCount] = useState('');
    const [recurrenceEndDateError, setRecurrenceEndDateError] = useState<
        string | undefined
    >();
    const [applyToFollowing, setApplyToFollowing] = useState(false);

    const isRecurrenceAvailable =
        !isEditMode &&
        !props.isMarketplaceBuyerContext &&
        props.meetingScheduleType === MeetingScheduleType.Scheduled &&
        participantsType === MeetingParticipantsType.BuyerSellerAdvisor;
    const isRepeating = isRecurrenceAvailable && repeatFrequency !== '';
    const maximumOccurrenceCount = isRepeating
        ? MAXIMUM_OCCURRENCE_COUNT[repeatFrequency as MeetingRecurrenceFrequency]
        : 0;

    const roundUpToNext15Minutes = (date: Date): Date => {
        const roundedDate = new Date(date);
        const minutes = roundedDate.getMinutes();
        const roundedMinutes = Math.ceil(minutes / 15) * 15;

        if (roundedMinutes === 60) {
            roundedDate.setHours(roundedDate.getHours() + 1);
            roundedDate.setMinutes(0);
        } else {
            roundedDate.setMinutes(roundedMinutes);
        }

        roundedDate.setSeconds(0);
        roundedDate.setMilliseconds(0);

        return roundedDate;
    };

    const now = new Date();
    const roundedNow = roundUpToNext15Minutes(now);
    const oneHourLater = new Date(roundedNow.getTime() + 60 * 60 * 1000);
    const initialStart = props.meetingToEdit?.dateStart
        ? new Date(props.meetingToEdit.dateStart)
        : roundedNow;
    const initialEnd = props.meetingToEdit?.dateEnd
        ? new Date(props.meetingToEdit.dateEnd)
        : oneHourLater;
    // An edited meeting opens in the zone it was scheduled in; otherwise the browser's zone.
    const scheduledTimeZone = props.meetingToEdit?.timeZone
        ? TimeZoneHelper.getTimeZoneByAbbreviation(props.meetingToEdit.timeZone)
        : undefined;
    const getInitialTimeZone = (date: Date) =>
        scheduledTimeZone && TimeZoneHelper.hasTimeZone(date, scheduledTimeZone)
            ? scheduledTimeZone
            : TimeZoneHelper.getTimeZoneByDate(date);
    const initialStartTimeZone = getInitialTimeZone(initialStart);
    const initialEndTimeZone = getInitialTimeZone(initialEnd);
    const startDateWithOffset = TimeZoneHelper.parseZoneDate(
        initialStart,
        initialStartTimeZone
    );
    const endDateWithOffset = TimeZoneHelper.parseZoneDate(
        initialEnd,
        initialEndTimeZone
    );
    const [startDate, setStartDate] = useState<Date>(startDateWithOffset);
    const [startTimeZone, setStartTimeZone] =
        useState<TimeZone>(initialStartTimeZone);
    const [endDate, setEndDate] = useState<Date>(endDateWithOffset);
    const [endTimeZone, setEndTimeZone] =
        useState<TimeZone>(initialEndTimeZone);

    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
    const [existingFiles, setExistingFiles] = useState(
        props.meetingToEdit?.files ?? []
    );
    const maxFileSize = 52428800;
    const maxFileSizeMb = 50;
    const maxFileNumber = 10;

    const [shouldValidateDateTime, setShouldValidateDateTime] = useState(false);

    const removeExistingFile = (fileId: number) => {
        setExistingFiles(existingFiles.filter((f) => f.id !== fileId));
    };

    const selectFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (filesToUpload.length + existingFiles.length >= maxFileNumber) {
            createNotification(
                `You can only upload a maximum of ${maxFileNumber} files`,
                'error'
            );
            event.target.value = null;
            return;
        }
        if (
            filesToUpload.every(
                (f) =>
                    f.name.toLowerCase() !==
                    event.target.files[0].name.toLowerCase()
            )
        ) {
            const file = event.target.files[0];
            if (file.size > maxFileSize) {
                createNotification(
                    `Max file size is ${maxFileSizeMb} Mb.`,
                    'error'
                );
                return;
            }
            if (file.size < 1) {
                createNotification(
                    'You can not upload an empty file.',
                    'error'
                );
                return;
            }
            setFilesToUpload(filesToUpload.concat([event.target.files[0]]));
        }
        event.target.value = null;
    };

    const deleteFileToUpload = (file: File) => {
        const index = filesToUpload.indexOf(file);
        if (index > -1) {
            filesToUpload.splice(index, 1);
            setFilesToUpload([...filesToUpload]);
        }
    };

    const downloadNewSelectedFile = (file: File) => {
        const fr = new FileReader();
        fr.readAsDataURL(file);
        const blob = new Blob([file], { type: file.type });
        const objectURL = window.URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = objectURL;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const getDateTimeValidation = () => {
        const valid = { date: true, time: true, zone: true };
        const invalid = { date: false, time: false, zone: false };
        const fail = (
            start: typeof valid,
            end: typeof valid,
            message: string
        ) => ({ start, end, message, isValid: false });

        let dateStart: Date;
        let dateEnd: Date;

        if (props.meetingScheduleType === MeetingScheduleType.Instant) {
            const now = new Date();
            const twoMinutesLater = new Date(now.getTime() + 2 * 60 * 1000);
            dateStart = twoMinutesLater;
            dateEnd = new Date(twoMinutesLater.getTime() + 30 * 60 * 1000);
        } else {
            dateStart = TimeZoneHelper.parseUtcDate(startDate, startTimeZone);
            dateEnd = TimeZoneHelper.parseUtcDate(endDate, endTimeZone);
        }

        const difference = DateTimeHelper.differenceInMinutes(
            dateEnd,
            dateStart
        );

        if (dateStart > dateEnd || difference == 0) {
            return fail(invalid, invalid, 'The meeting must end after it starts');
        }

        if (!isStartLocked && dateStart < new Date()) {
            return fail(invalid, invalid, 'The meeting must start in the future');
        }

        const isEndChanged =
            !props.meetingToEdit?.dateEnd ||
            new Date(props.meetingToEdit.dateEnd).getTime() !==
                dateEnd.getTime();

        if (isStartLocked && isEndChanged && dateEnd <= new Date()) {
            return fail(
                valid,
                invalid,
                'The end of an in-progress meeting must be in the future'
            );
        }

        if ((dateEnd.getTime() - dateStart.getTime()) / 36e5 > 12) {
            return fail(
                invalid,
                invalid,
                'The maximum possible duration of the Meeting is 12 hours'
            );
        }

        return { start: valid, end: valid, message: undefined, isValid: true };
    };

    // Worked out from the values on screen at every render, so the check never runs on the previous values.
    const dateTimeValidation = getDateTimeValidation();
    const startDateTimeState = dateTimeValidation.start;
    const endDateTimeState = dateTimeValidation.end;
    const dateErrorMessage = shouldValidateDateTime
        ? dateTimeValidation.message
        : undefined;

    const uploadFiles = async (files: File[]): Promise<number[]> => {
        if (files.length === 0) {
            return [];
        }

        const formFiles = files.map((f) => {
            const formData = new FormData();
            formData.append('File', f);
            return formData;
        });

        const requests = formFiles.map((f) =>
            axios.post(`/api/temporaryfiles`, f, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
        );

        const responses = await Promise.all(requests);

        if (responses.some((r) => r.status !== 200)) {
            createNotification(
                'Some files are not uploaded due to an unexpected error.',
                'error'
            );
            throw new Error('Failed to upload files');
        }

        return responses
            .filter((r) => r.data && r.status === 200)
            .map((r) => r.data.id);
    };

    const getRecurrenceUnit = (): moment.unitOfTime.DurationConstructor =>
        repeatFrequency === MeetingRecurrenceFrequency.Monthly
            ? 'month'
            : 'week';

    const getEarliestRecurrenceEndDate = () =>
        moment(startDate).startOf('day').add(1, getRecurrenceUnit());

    const getLatestRecurrenceEndDate = () =>
        moment(startDate)
            .startOf('day')
            .add(maximumOccurrenceCount - 1, getRecurrenceUnit());

    const getRecurrenceEndTooltip = (): string => {
        const frequencyLabel =
            MEETING_RECURRENCE_FREQUENCY_LABELS[
                repeatFrequency as MeetingRecurrenceFrequency
            ].toLowerCase();
        const meetingRange = `A ${frequencyLabel} series can have ${MINIMUM_OCCURRENCE_COUNT} to ${maximumOccurrenceCount} meetings.`;
        if (recurrenceEndType === RecurrenceEndType.AfterCount) {
            return `The series ends after this many meetings, the first one included. ${meetingRange}`;
        }
        return `The series ends on this date. Pick a date from ${getEarliestRecurrenceEndDate().format(RECURRENCE_DATE_FORMAT)} to ${getLatestRecurrenceEndDate().format(RECURRENCE_DATE_FORMAT)}. ${meetingRange}`;
    };

    const getRecurrenceEndDateError = (): string | undefined => {
        if (!recurrenceEndDate) {
            return 'End date is required';
        }
        const endDay = moment(recurrenceEndDate).startOf('day');
        if (endDay.isBefore(getEarliestRecurrenceEndDate())) {
            return 'End date must allow at least two meetings';
        }
        if (endDay.isAfter(getLatestRecurrenceEndDate())) {
            return `End date must allow at most ${maximumOccurrenceCount} meetings`;
        }
        return undefined;
    };

    const buildRecurrence = (): MeetingRecurrenceContract | undefined => {
        if (!isRepeating) {
            return undefined;
        }
        const frequency = repeatFrequency as MeetingRecurrenceFrequency;
        if (recurrenceEndType === RecurrenceEndType.AfterCount) {
            return {
                frequency: frequency,
                occurrenceCount: Number(occurrenceCount),
                timeZone: startTimeZone.abbreviation,
            };
        }
        const endDateMidday = new Date(recurrenceEndDate);
        endDateMidday.setHours(RECURRENCE_END_DATE_HOUR, 0, 0, 0);
        return {
            frequency: frequency,
            endDate: TimeZoneHelper.parseUtcDate(endDateMidday, startTimeZone),
            timeZone: startTimeZone.abbreviation,
        };
    };

    const prepareEngagementMeetingContract =
        async (): Promise<PostEngagementConferenceContract> => {
            if (participantsType === '') {
                throw new Error('Participants Type is required');
            }

            let dateStart: Date | undefined;
            let dateEnd: Date | undefined;

            if (props.meetingScheduleType === MeetingScheduleType.Scheduled) {
                dateStart = TimeZoneHelper.parseUtcDate(
                    startDate,
                    startTimeZone
                );
                dateEnd = TimeZoneHelper.parseUtcDate(endDate, endTimeZone);
            } else if (
                props.meetingScheduleType === MeetingScheduleType.Instant
            ) {
                const now = new Date();
                const twoMinutesLater = new Date(now.getTime() + 2 * 60 * 1000);
                dateStart = twoMinutesLater;
                dateEnd = new Date(twoMinutesLater.getTime() + 30 * 60 * 1000);
            }

            const uploadedFileIds = await uploadFiles(filesToUpload);

            return {
                name: meetingTitle,
                message: message || '',
                dateStart: dateStart,
                dateEnd: dateEnd,
                files: [],
                participantsType: participantsType as MeetingParticipantsType,
                format: MeetingFormat.Virtual,
                ScheduleType: props.meetingScheduleType,
                buyerId:
                    selectedBuyerId !== ''
                        ? (selectedBuyerId as number)
                        : undefined,
                posterId: currentUser.id,
                unregisteredFilesIds: uploadedFileIds,
                recurrence: buildRecurrence(),
                timeZone:
                    props.meetingScheduleType === MeetingScheduleType.Scheduled
                        ? startTimeZone.abbreviation
                        : undefined,
            };
        };

    const handleSubmit = async () => {
        if (props.meetingScheduleType === MeetingScheduleType.Scheduled) {
            setShouldValidateDateTime(true);

            if (!dateTimeValidation.isValid) {
                return;
            }
        }

        if (isRepeating && recurrenceEndType === RecurrenceEndType.OnDate) {
            const endDateError = getRecurrenceEndDateError();
            setRecurrenceEndDateError(endDateError);
            if (endDateError) {
                return;
            }
        }

        setIsSubmitting(true);

        if (isEditMode) {
            try {
                const uploadedFileIds = await uploadFiles(filesToUpload);
                const removedFileIds = (props.meetingToEdit.files ?? [])
                    .filter((f) => !existingFiles.some((e) => e.id === f.id))
                    .map((f) => f.id);

                const contract: UpdateSupportMeetingContract = {
                    name: meetingTitle,
                    message: message || '',
                    dateStart: isStartLocked
                        ? undefined
                        : TimeZoneHelper.parseUtcDate(startDate, startTimeZone),
                    dateEnd: TimeZoneHelper.parseUtcDate(endDate, endTimeZone),
                    removedFileIds: removedFileIds,
                    unregisteredFilesIds: uploadedFileIds,
                    applyToFollowing: props.meetingToEdit.seriesId
                        ? applyToFollowing
                        : undefined,
                    timeZone: startTimeZone.abbreviation,
                };

                const result = await dispatch(
                    UpdateSupportMeeting({
                        meetingEventId: props.meetingToEdit.id,
                        contract: contract,
                    })
                );

                if (UpdateSupportMeeting.fulfilled.match(result)) {
                    props.onMeetingUpdated?.();
                    props.onClose();
                }
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        if (props.isMarketplaceBuyerContext) {
            const uploadedFileIds = await uploadFiles(filesToUpload);

            const meetingData = {
                BuyerId: props.marketplaceBuyerId,
                Name: meetingTitle,
                Message: message || '',
                UnregisteredFilesIds: uploadedFileIds,
            };

            try {
                const response = await axios.post(
                    '/api/Marketplace/MarketplaceMeetings',
                    meetingData
                );
                if (response.status === 200) {
                    createNotification(
                        'Meeting created successfully',
                        'success'
                    );
                    const eventId = response.data;
                    props.onClose();
                    setTimeout(() => {
                        const navigationEvent = new CustomEvent(
                            'navigateToMeetings',
                            {
                                detail: { eventId: eventId },
                            }
                        );
                        window.dispatchEvent(navigationEvent);
                    }, 100);
                } else {
                    if (
                        response?.status === 409 &&
                        props.isMarketplaceBuyerContext
                    ) {
                        createNotification(
                            'Support Meeting is in progress for the buyer.',
                            'error'
                        );
                    } else {
                        createNotification('Failed to create meeting', 'error');
                    }
                }
            } catch (error: any) {
                createNotification(
                    'An error occurred while creating the meeting',
                    'error'
                );
            }
        } else {
            const contract = await prepareEngagementMeetingContract();
            const result = await dispatch(
                AddEngagementConference({
                    engagementId: props.engagementId,
                    contract: contract,
                })
            );

            // Refreshed on an error too: the meeting can be saved before a later step fails,
            // and its tile must not wait for a page reload.
            await dispatch(FetchEngagementConferences(props.engagementId));
            if (AddEngagementConference.fulfilled.match(result)) {
                props.onClose();
            }
        }

        setIsSubmitting(false);
    };

    // Keeps the zone the user picked while it exists on the new date (e.g. CDT all summer),
    // otherwise falls back to the browser's zone for that date.
    const keepTimeZone = (date: Date, zone: TimeZone) =>
        zone && TimeZoneHelper.hasTimeZone(date, zone)
            ? zone
            : TimeZoneHelper.getTimeZoneByDate(date);

    const handleStartDateChange = (value: Date) => {
        if (!value) {
            return;
        }
        const newStartTimeZone = keepTimeZone(value, startTimeZone);
        const newEndDate = new Date(value.getTime() + ONE_HOUR_MS);
        setStartDate(value);
        setStartTimeZone(newStartTimeZone);
        setEndDate(newEndDate);
        setEndTimeZone(keepTimeZone(newEndDate, newStartTimeZone));
    };

    const handleStartTimeZoneChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const zone = TimeZoneHelper.getTimeZoneByAbbreviation(
            event.target.value
        );
        setStartTimeZone(zone);
        // The end moves into the start's zone and keeps its clock time.
        if (TimeZoneHelper.hasTimeZone(endDate, zone)) {
            setEndTimeZone(zone);
        }
    };

    const handleEndDateChange = (value: Date) => {
        if (!value) {
            return;
        }
        setEndDate(value);
        setEndTimeZone(keepTimeZone(value, endTimeZone));
    };

    const handleEndTimeZoneChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const zone = TimeZoneHelper.getTimeZoneByAbbreviation(
            event.target.value
        );
        if (TimeZoneHelper.hasTimeZone(endDate, zone)) {
            setEndTimeZone(zone);
        }
    };

    const handleParticipantsTypeChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const newValue =
            e.target.value === ''
                ? ''
                : (Number(e.target.value) as MeetingParticipantsType);
        setParticipantsType(newValue);
        if (!BUYER_REQUIRED_PARTICIPANT_TYPES.includes(newValue)) {
            setSelectedBuyerId('');
        }
    };

    return (
        <div className="schedule-virtual-meeting-popup">
            <Rodal
                width={550}
                visible={true}
                animation={'slideRight'}
                onClose={props.onClose}
                onMaskClick={() => setIsDiscardConfirmationVisible(true)}
                center>
                <ModalHeader>
                    {isEditMode
                        ? 'Edit Meeting'
                        : props.meetingScheduleType ===
                            MeetingScheduleType.Instant
                          ? 'Launch Instant Meeting'
                          : 'Schedule Virtual Meeting'}
                </ModalHeader>
                <ModalBody className="body_white_box">
                    <Form
                        initialValues={{
                            meetingTitle: props.meetingToEdit?.name ?? '',
                            message: props.meetingToEdit?.message ?? '',
                            participantsType: participantsType,
                            selectedBuyerId: '',
                            occurrenceCount: '',
                        }}
                        validationSchema={yup.object().shape({
                            meetingTitle: yup
                                .string()
                                .required('Meeting Title is required'),
                            message: yup
                                .string()
                                .required('Message is required'),
                            ...(props.isMarketplaceBuyerContext || isEditMode
                                ? {}
                                : {
                                      participantsType: yup
                                          .number()
                                          .required(
                                              'Participants Type is required'
                                          )
                                          .min(
                                              0,
                                              'Please select a Participants Type'
                                          ),
                                      selectedBuyerId: yup
                                          .number()
                                          .when('participantsType', {
                                              is: (val: any) =>
                                                  BUYER_REQUIRED_PARTICIPANT_TYPES.includes(
                                                      Number(val)
                                                  ),
                                              then: (schema) =>
                                                  schema.required(
                                                      'Buyer is required'
                                                  ),
                                              otherwise: (schema) =>
                                                  schema.notRequired(),
                                          }),
                                  }),
                            ...(isRepeating &&
                            recurrenceEndType === RecurrenceEndType.AfterCount
                                ? {
                                      occurrenceCount: yup
                                          .number()
                                          .typeError(
                                              'Number of meetings is required'
                                          )
                                          .integer(
                                              'Number of meetings must be a whole number'
                                          )
                                          .min(
                                              MINIMUM_OCCURRENCE_COUNT,
                                              `Minimum is ${MINIMUM_OCCURRENCE_COUNT} meetings`
                                          )
                                          .max(
                                              maximumOccurrenceCount,
                                              `Maximum is ${maximumOccurrenceCount} meetings`
                                          )
                                          .required(
                                              'Number of meetings is required'
                                          ),
                                  }
                                : {}),
                        })}
                        innerRef={modalForm}
                        onSubmit={handleSubmit}>
                        <Col>
                            <FormGroup>
                                <Row>
                                    <Col md={12}>
                                        <label>Meeting Title</label>
                                        <Input
                                            name="meetingTitle"
                                            value={meetingTitle}
                                            onChange={(e) =>
                                                setMeetingTitle(e.target.value)
                                            }
                                            placeholder="Enter meeting title"
                                            className="form-control"
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            {isEditMode && props.meetingToEdit.seriesId && (
                                <FormGroup>
                                    <Row>
                                        <Col md={12}>
                                            <label>Apply changes to</label>
                                            <MeetingSeriesScopeSelector
                                                name="edit-meeting-scope"
                                                applyToFollowing={
                                                    applyToFollowing
                                                }
                                                onChange={setApplyToFollowing}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>
                            )}

                            {(props.meetingScheduleType ===
                                MeetingScheduleType.Scheduled ||
                                isEditMode) && (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <div>
                                                <label>Start Date</label>
                                            </div>
                                            <InputGroup
                                                className={
                                                    'small-border group-order-field' +
                                                    (!shouldValidateDateTime ||
                                                    startDateTimeState.date
                                                        ? ''
                                                        : ' required-field') +
                                                    (isStartLocked
                                                        ? ' disabled-field'
                                                        : '')
                                                }
                                                aria-invalid={
                                                    shouldValidateDateTime &&
                                                    !startDateTimeState.date
                                                }
                                                onClick={() => {
                                                    if (isStartLocked) {
                                                        return;
                                                    }
                                                    if (startDateRef.current) {
                                                        startDateRef.current.setOpen(
                                                            true
                                                        );
                                                    }
                                                }}>
                                                <InputGroupAddon addonType="prepend">
                                                    <div className="input-group-text">
                                                        <FontAwesomeIcon
                                                            icon={faCalendarAlt}
                                                        />
                                                    </div>
                                                </InputGroupAddon>
                                                <DatePicker
                                                    ref={startDateRef}
                                                    className="form-control"
                                                    selected={startDate}
                                                    disabled={isStartLocked}
                                                    showTimeSelect
                                                    timeIntervals={15}
                                                    onChange={
                                                        handleStartDateChange
                                                    }
                                                    onCalendarClose={() => {
                                                        setShouldValidateDateTime(
                                                            true
                                                        );
                                                    }}
                                                    dateFormat="MM/d/yyyy hh:mm a"
                                                    customInput={
                                                        <DateControlInput />
                                                    }
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col md={6}>
                                            <div>
                                                <label>Time Zone</label>
                                            </div>
                                            <select
                                                value={
                                                    startTimeZone.abbreviation
                                                }
                                                name="start-time-zone"
                                                disabled={isStartLocked}
                                                onChange={
                                                    handleStartTimeZoneChange
                                                }
                                                className={
                                                    'form-control shadow-off' +
                                                    (!shouldValidateDateTime ||
                                                    startDateTimeState.zone
                                                        ? ''
                                                        : ' required-field') +
                                                    (isStartLocked
                                                        ? ' disabled-field'
                                                        : '')
                                                }
                                                aria-invalid={
                                                    shouldValidateDateTime &&
                                                    !startDateTimeState.zone
                                                }>
                                                {TimeZoneHelper.getDateZones(
                                                    startDate
                                                ).map(
                                                    (
                                                        dateZone: TimeZone,
                                                        index: number
                                                    ) => (
                                                        <option
                                                            key={
                                                                'start-zone' +
                                                                index
                                                            }
                                                            value={
                                                                dateZone.abbreviation
                                                            }>
                                                            {
                                                                dateZone.abbreviation
                                                            }
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <div>
                                                <label>End Date</label>
                                            </div>
                                            <InputGroup
                                                className={
                                                    'small-border group-order-field' +
                                                    (!shouldValidateDateTime ||
                                                    endDateTimeState.date
                                                        ? ''
                                                        : ' required-field')
                                                }
                                                aria-invalid={
                                                    shouldValidateDateTime &&
                                                    !endDateTimeState.date
                                                }
                                                onClick={() => {
                                                    if (endDateRef.current) {
                                                        endDateRef.current.setOpen(
                                                            true
                                                        );
                                                    }
                                                }}>
                                                <InputGroupAddon addonType="prepend">
                                                    <div className="input-group-text">
                                                        <FontAwesomeIcon
                                                            icon={faCalendarAlt}
                                                        />
                                                    </div>
                                                </InputGroupAddon>
                                                <DatePicker
                                                    ref={endDateRef}
                                                    className="form-control"
                                                    selected={endDate}
                                                    showTimeSelect
                                                    timeIntervals={15}
                                                    onChange={
                                                        handleEndDateChange
                                                    }
                                                    onCalendarClose={() => {
                                                        setShouldValidateDateTime(
                                                            true
                                                        );
                                                    }}
                                                    dateFormat="MM/d/yyyy hh:mm a"
                                                    customInput={
                                                        <DateControlInput />
                                                    }
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col md={6}>
                                            <div>
                                                <label>Time Zone</label>
                                            </div>
                                            <select
                                                value={endTimeZone.abbreviation}
                                                name="end-time-zone"
                                                onChange={
                                                    handleEndTimeZoneChange
                                                }
                                                className={
                                                    'form-control shadow-off' +
                                                    (!shouldValidateDateTime ||
                                                    endDateTimeState.zone
                                                        ? ''
                                                        : ' required-field')
                                                }
                                                aria-invalid={
                                                    shouldValidateDateTime &&
                                                    !endDateTimeState.zone
                                                }>
                                                {TimeZoneHelper.getDateZones(
                                                    endDate
                                                ).map(
                                                    (
                                                        dateZone: TimeZone,
                                                        index: number
                                                    ) => (
                                                        <option
                                                            key={
                                                                'end-zone' +
                                                                index
                                                            }
                                                            value={
                                                                dateZone.abbreviation
                                                            }>
                                                            {
                                                                dateZone.abbreviation
                                                            }
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </Col>
                                    </Row>
                                    {dateErrorMessage ? (
                                        <Row>
                                            <Col md={12}>
                                                <label className="error-message">
                                                    {dateErrorMessage}
                                                </label>
                                            </Col>
                                        </Row>
                                    ) : (
                                        []
                                    )}
                                </>
                            )}

                            {!props.isMarketplaceBuyerContext && !isEditMode && (
                                <FormGroup>
                                    <Row>
                                        <Col md={12}>
                                            <label>Participants Type</label>
                                            <Input
                                                type="select"
                                                name="participantsType"
                                                value={participantsType}
                                                onChange={
                                                    handleParticipantsTypeChange
                                                }
                                                className="shadow-off form-control">
                                                <option value="">Select</option>
                                                {Object.entries(
                                                    getParticipantsTypeOptions()
                                                ).map(([key, value]) => (
                                                    <option
                                                        key={key}
                                                        value={key}>
                                                        {value}
                                                    </option>
                                                ))}
                                            </Input>
                                        </Col>
                                    </Row>
                                </FormGroup>
                            )}

                            {BUYER_REQUIRED_PARTICIPANT_TYPES.includes(
                                participantsType
                            ) && (
                                <FormGroup>
                                    <Row>
                                        <Col md={12}>
                                            <label>Buyer</label>
                                            <Input
                                                type="select"
                                                name="selectedBuyerId"
                                                value={selectedBuyerId}
                                                onChange={(e) =>
                                                    setSelectedBuyerId(
                                                        e.target.value === ''
                                                            ? ''
                                                            : Number(
                                                                  e.target.value
                                                              )
                                                    )
                                                }
                                                className="shadow-off form-control">
                                                <option value="">Select</option>
                                                {getSelectableBuyers().map(
                                                    (buyer) => (
                                                        <option
                                                            key={buyer.id}
                                                            value={buyer.id}>
                                                            {buyer.name}
                                                        </option>
                                                    )
                                                )}
                                            </Input>
                                        </Col>
                                    </Row>
                                </FormGroup>
                            )}

                            {isRecurrenceAvailable && (
                                <FormGroup>
                                    <Row>
                                        <Col md={12}>
                                            <label>Repeats</label>
                                            <Input
                                                type="select"
                                                name="repeatFrequency"
                                                value={repeatFrequency}
                                                onChange={(e) =>
                                                    setRepeatFrequency(
                                                        e.target.value === ''
                                                            ? ''
                                                            : (Number(
                                                                  e.target.value
                                                              ) as MeetingRecurrenceFrequency)
                                                    )
                                                }
                                                className="shadow-off form-control">
                                                <option value="">
                                                    Does not repeat
                                                </option>
                                                {Object.entries(
                                                    MEETING_RECURRENCE_FREQUENCY_LABELS
                                                ).map(([key, label]) => (
                                                    <option
                                                        key={key}
                                                        value={key}>
                                                        {label}
                                                    </option>
                                                ))}
                                            </Input>
                                        </Col>
                                    </Row>
                                </FormGroup>
                            )}

                            {isRepeating && (
                                <FormGroup>
                                    <Row>
                                        <Col md={6}>
                                            <label>Series Ends</label>
                                            <Input
                                                type="select"
                                                name="recurrenceEndType"
                                                value={recurrenceEndType}
                                                onChange={(e) =>
                                                    setRecurrenceEndType(
                                                        e.target
                                                            .value as RecurrenceEndType
                                                    )
                                                }
                                                className="shadow-off form-control">
                                                <option
                                                    value={
                                                        RecurrenceEndType.OnDate
                                                    }>
                                                    On a date
                                                </option>
                                                <option
                                                    value={
                                                        RecurrenceEndType.AfterCount
                                                    }>
                                                    After a number of meetings
                                                </option>
                                            </Input>
                                        </Col>
                                        <Col md={6}>
                                            <label>
                                                {recurrenceEndType ===
                                                RecurrenceEndType.OnDate
                                                    ? 'Series End Date'
                                                    : 'Number of Meetings'}
                                                <FontAwesomeIcon
                                                    id={
                                                        RECURRENCE_END_TOOLTIP_ID
                                                    }
                                                    icon={faInfoCircle}
                                                    className="recurrence-end-info"
                                                />
                                            </label>
                                            <PortalTooltip
                                                anchorId={
                                                    RECURRENCE_END_TOOLTIP_ID
                                                }
                                                delayShow={
                                                    RECURRENCE_END_TOOLTIP_DELAY_MS
                                                }
                                                place="top"
                                                content={getRecurrenceEndTooltip()}
                                            />
                                            {recurrenceEndType ===
                                            RecurrenceEndType.OnDate ? (
                                                <InputGroup
                                                    className={
                                                        'small-border group-order-field' +
                                                        (recurrenceEndDateError
                                                            ? ' required-field'
                                                            : '')
                                                    }
                                                    onClick={() => {
                                                        if (
                                                            recurrenceEndDateRef.current
                                                        ) {
                                                            recurrenceEndDateRef.current.setOpen(
                                                                true
                                                            );
                                                        }
                                                    }}>
                                                    <InputGroupAddon addonType="prepend">
                                                        <div className="input-group-text">
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faCalendarAlt
                                                                }
                                                            />
                                                        </div>
                                                    </InputGroupAddon>
                                                    <DatePicker
                                                        ref={
                                                            recurrenceEndDateRef
                                                        }
                                                        className="form-control"
                                                        selected={
                                                            recurrenceEndDate
                                                        }
                                                        minDate={getEarliestRecurrenceEndDate().toDate()}
                                                        maxDate={getLatestRecurrenceEndDate().toDate()}
                                                        onChange={(
                                                            value: Date
                                                        ) => {
                                                            setRecurrenceEndDate(
                                                                value
                                                            );
                                                            setRecurrenceEndDateError(
                                                                undefined
                                                            );
                                                        }}
                                                        dateFormat="MM/d/yyyy"
                                                        placeholderText="Select end date"
                                                        customInput={
                                                            <DateControlInput />
                                                        }
                                                    />
                                                </InputGroup>
                                            ) : (
                                                <InputGroup className="occurrence-count-field">
                                                    <Input
                                                        type="number"
                                                        name="occurrenceCount"
                                                        value={occurrenceCount}
                                                        min={
                                                            MINIMUM_OCCURRENCE_COUNT
                                                        }
                                                        max={
                                                            maximumOccurrenceCount
                                                        }
                                                        onChange={(e) =>
                                                            setOccurrenceCount(
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder={`${MINIMUM_OCCURRENCE_COUNT} - ${maximumOccurrenceCount}`}
                                                        className="form-control"
                                                    />
                                                    <InputGroupAddon addonType="append">
                                                        <div className="input-group-text">
                                                            meetings
                                                        </div>
                                                    </InputGroupAddon>
                                                </InputGroup>
                                            )}
                                        </Col>
                                    </Row>
                                    {recurrenceEndDateError &&
                                        recurrenceEndType ===
                                            RecurrenceEndType.OnDate && (
                                            <Row>
                                                <Col md={12}>
                                                    <label className="error-message">
                                                        {recurrenceEndDateError}
                                                    </label>
                                                </Col>
                                            </Row>
                                        )}
                                </FormGroup>
                            )}

                            <FormGroup>
                                <Row>
                                    <Col md={12}>
                                        <label>Message</label>
                                        <Input
                                            type="textarea"
                                            name="message"
                                            value={message}
                                            onChange={(e) =>
                                                setMessage(e.target.value)
                                            }
                                            maxLength={140}
                                            placeholder="Enter Comment here"
                                            className="form-control"
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row>
                                    <Col md={12}>
                                        <div className="form-group">
                                            <div className="header-container">
                                                <label>Related Items</label>
                                                <label
                                                    className="link-button"
                                                    htmlFor="upload-file">
                                                    Add File
                                                </label>
                                                <input
                                                    hidden={true}
                                                    type="file"
                                                    name="file"
                                                    id="upload-file"
                                                    className="upload-file-input"
                                                    onChange={selectFileHandler}
                                                />
                                            </div>
                                            {existingFiles.map(
                                                (file, index) => (
                                                    <div
                                                        key={
                                                            'existing-file' +
                                                            index
                                                        }
                                                        className="file-container">
                                                        <InputGroup>
                                                            <input
                                                                className="form-control file"
                                                                disabled
                                                                value={
                                                                    file.name
                                                                }
                                                            />
                                                            <InputGroupAddon addonType="append">
                                                                <div className="input-group-text file-addon">
                                                                    <FontAwesomeIcon
                                                                        className="trash-icon red"
                                                                        icon={
                                                                            faTrash
                                                                        }
                                                                        onClick={() => {
                                                                            removeExistingFile(
                                                                                file.id
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            </InputGroupAddon>
                                                        </InputGroup>
                                                    </div>
                                                )
                                            )}
                                            {filesToUpload.map(
                                                (file, index) => (
                                                    <div
                                                        key={'file' + index}
                                                        className="file-container">
                                                        <InputGroup>
                                                            <input
                                                                className="form-control file"
                                                                disabled
                                                                value={
                                                                    file.name
                                                                }
                                                            />
                                                            <InputGroupAddon addonType="append">
                                                                <div className="input-group-text file-addon no-right-padding">
                                                                    <FontAwesomeIcon
                                                                        className="trash-icon"
                                                                        icon={
                                                                            faDownload
                                                                        }
                                                                        onClick={() => {
                                                                            downloadNewSelectedFile(
                                                                                file
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            </InputGroupAddon>
                                                            <InputGroupAddon addonType="append">
                                                                <div className="input-group-text file-addon">
                                                                    <FontAwesomeIcon
                                                                        className="trash-icon red"
                                                                        icon={
                                                                            faTrash
                                                                        }
                                                                        onClick={() => {
                                                                            deleteFileToUpload(
                                                                                file
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            </InputGroupAddon>
                                                        </InputGroup>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </FormGroup>
                        </Col>
                        <Col className="footer">
                            <Button
                                className="cancel btn btn-secondary"
                                onClick={() => props.onClose()}>
                                Cancel
                            </Button>
                            <ButtonLoader
                                type="submit"
                                className="btn btn-primary loader-submit"
                                disabled={isSubmitting}
                                isLoading={isSubmitting}
                                buttonText={
                                    isEditMode
                                        ? 'Save'
                                        : props.meetingScheduleType ===
                                            MeetingScheduleType.Instant
                                          ? 'Launch Meeting'
                                          : 'Schedule Meeting'
                                }
                                loaderButtonText=""
                                onClick={() => {
                                    setShouldValidateDateTime(true);
                                    modalForm.current.handleSubmit();
                                }}
                            />
                        </Col>
                    </Form>
                </ModalBody>
            </Rodal>
            <ConfirmationModal
                isVisible={isDiscardConfirmationVisible}
                setIsVisible={setIsDiscardConfirmationVisible}
                header="Discard Meeting"
                confirmationText="Are you sure you want to close and discard the meeting pop-up?"
                nextButtonText="Yes"
                cancelButtonText="No"
                onConfirm={() => props.onClose()}
            />
        </div>
    );
};

export default ScheduleVirtualMeetingModal;
